import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { AuthContext } from "../../context/Auth/AuthContext";
import apiV2 from "../../services/apiV2";
import toastError from "../../errors/toastError";
import "./personal-assistant.css";

const TICKET_REF_RE = /(ticket:#|#)(\d+)/gi;

function renderMessageContent(text, onTicketClick) {
	const raw = String(text || "");
	const parts = [];
	let lastIndex = 0;
	let match;
	const re = new RegExp(TICKET_REF_RE.source, "gi");
	while ((match = re.exec(raw)) !== null) {
		if (match.index > lastIndex) {
			parts.push(raw.slice(lastIndex, match.index));
		}
		const id = match[2];
		parts.push(
			<button
				key={`t-${match.index}-${id}`}
				type="button"
				className="pa-ticket-link"
				onClick={() => onTicketClick(id)}
			>
				#{id}
			</button>
		);
		lastIndex = match.index + match[0].length;
	}
	if (lastIndex < raw.length) {
		parts.push(raw.slice(lastIndex));
	}
	return parts.length ? parts : raw;
}

function SparkIcon({ size = 18 }) {
	return (
		<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
			<path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z" />
			<path d="M20 3v4" />
			<path d="M22 5h-4" />
			<path d="M4 17v2" />
			<path d="M5 18H3" />
		</svg>
	);
}

function IconClose() {
	return (
		<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
			<path d="M18 6 6 18" />
			<path d="m6 6 12 12" />
		</svg>
	);
}

function IconPlus() {
	return (
		<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
			<path d="M12 5v14" />
			<path d="M5 12h14" />
		</svg>
	);
}

function IconList() {
	return (
		<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
			<path d="M8 6h13" />
			<path d="M8 12h13" />
			<path d="M8 18h13" />
			<path d="M3 6h.01" />
			<path d="M3 12h.01" />
			<path d="M3 18h.01" />
		</svg>
	);
}

function IconSend() {
	return (
		<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
			<path d="m22 2-7 20-4-9-9-4Z" />
			<path d="M22 2 11 13" />
		</svg>
	);
}

const SUGGESTIONS = [
	"Como criar um ticket?",
	"Como fazer envio em massa?",
	"Como conectar o WhatsApp?",
];

function PersonalAssistant({ open, onClose }) {
	const { user } = useContext(AuthContext);
	const history = useHistory();
	const [threads, setThreads] = useState([]);
	const [activeThreadId, setActiveThreadId] = useState(null);
	const [messages, setMessages] = useState([]);
	const [usage, setUsage] = useState(null);
	const [input, setInput] = useState("");
	const [loading, setLoading] = useState(false);
	const [sending, setSending] = useState(false);
	const [view, setView] = useState("chat");
	const bottomRef = useRef(null);
	const textareaRef = useRef(null);

	const canUse =
		Boolean(user?.canUsePersonalAi) ||
		user?.profile === "admin" ||
		user?.profile === "master" ||
		Boolean(user?.personalAiUnlimited);

	const refreshUsage = useCallback(async () => {
		try {
			const { data } = await apiV2.get("/assistant/usage");
			setUsage(data);
		} catch {
			// ignore
		}
	}, []);

	const loadThreads = useCallback(async () => {
		try {
			const { data } = await apiV2.get("/assistant/threads");
			setThreads(Array.isArray(data?.threads) ? data.threads : []);
		} catch (err) {
			toastError(err);
		}
	}, []);

	const loadMessages = useCallback(async (threadId) => {
		if (!threadId) {
			setMessages([]);
			return;
		}
		setLoading(true);
		try {
			const { data } = await apiV2.get(`/assistant/threads/${threadId}/messages`);
			setMessages(Array.isArray(data?.messages) ? data.messages : []);
		} catch (err) {
			toastError(err);
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		if (!canUse || !open) return undefined;
		refreshUsage();
		loadThreads();
		return undefined;
	}, [canUse, open, refreshUsage, loadThreads]);

	useEffect(() => {
		if (activeThreadId) loadMessages(activeThreadId);
	}, [activeThreadId, loadMessages]);

	useEffect(() => {
		bottomRef.current?.scrollIntoView({ behavior: "smooth" });
	}, [messages, open, sending]);

	useEffect(() => {
		if (open && view === "chat") {
			setTimeout(() => textareaRef.current?.focus(), 80);
		}
	}, [open, view]);

	const handleNewConversation = async () => {
		try {
			const { data } = await apiV2.post("/assistant/threads", { title: "Nova conversa" });
			await loadThreads();
			setActiveThreadId(data.id);
			setMessages([]);
			setView("chat");
		} catch (err) {
			toastError(err);
		}
	};

	const handleSend = async (overrideText) => {
		const text = String(overrideText ?? input ?? "").trim();
		if (!text || sending) return;

		let threadId = activeThreadId;
		setSending(true);
		try {
			if (!threadId) {
				const { data } = await apiV2.post("/assistant/threads", {
					title: text.slice(0, 60),
				});
				threadId = data.id;
				setActiveThreadId(threadId);
				await loadThreads();
			}

			const optimistic = {
				id: `tmp-${Date.now()}`,
				role: "user",
				content: text,
			};
			setMessages((prev) => [...prev, optimistic]);
			setInput("");

			const { data } = await apiV2.post(`/assistant/threads/${threadId}/messages`, {
				content: text,
			});
			setMessages((prev) => {
				const withoutTmp = prev.filter((m) => m.id !== optimistic.id);
				return [...withoutTmp, data.userMessage, data.assistantMessage].filter(Boolean);
			});
			if (data.usage) setUsage(data.usage);
			await loadThreads();
		} catch (err) {
			toastError(err);
		} finally {
			setSending(false);
		}
	};

	const openTicket = (ticketId) => {
		onClose?.();
		history.push(`/tickets/${ticketId}`);
	};

	if (!canUse || !open) return null;

	const used = usage?.used ?? 0;
	const limit = usage?.limit ?? 0;
	const unlimited = Boolean(usage?.unlimited) || Boolean(user?.personalAiUnlimited);
	const companyRemaining = usage?.companyRemaining ?? 0;
	const usagePct = unlimited
		? 0
		: limit > 0
			? Math.min(100, Math.round((used / limit) * 100))
			: 0;
	const blocked = usage && !usage.canUse;
	const userInitial = (user?.name || "U").trim().charAt(0).toUpperCase();
	const usageSelfLabel = unlimited
		? `Seu uso: ${used} (sem cota)`
		: `Seu uso: ${used}/${limit || "—"}`;

	return (
		<>
			<button type="button" className="pa-backdrop" aria-label="Fechar assistente" onClick={onClose} />
			<aside className="pa-panel" role="dialog" aria-label="Assistente geral">
				<header className="pa-header">
					<div className="pa-header-main">
						<div className="pa-avatar pa-avatar--ai" aria-hidden>
							<SparkIcon size={16} />
						</div>
						<div className="pa-header-text">
							<strong>Assistente geral</strong>
							<span className="pa-header-sub">Sempre online · respostas rápidas</span>
						</div>
					</div>
					<div className="pa-header-actions">
						<button
							type="button"
							className={`pa-icon-btn${view === "threads" ? " is-active" : ""}`}
							title="Conversas"
							aria-label="Conversas"
							onClick={() => setView(view === "threads" ? "chat" : "threads")}
						>
							<IconList />
						</button>
						<button
							type="button"
							className="pa-icon-btn"
							title="Nova conversa"
							aria-label="Nova conversa"
							onClick={handleNewConversation}
						>
							<IconPlus />
						</button>
						<button type="button" className="pa-icon-btn" onClick={onClose} aria-label="Fechar" title="Fechar">
							<IconClose />
						</button>
					</div>
				</header>

				<div className="pa-usage-bar">
					<div className="pa-usage-meta">
						<span>{usageSelfLabel}</span>
						<span>Empresa: {companyRemaining}</span>
					</div>
					<div className="pa-usage-track" aria-hidden>
						<div className="pa-usage-fill" style={{ width: `${usagePct}%` }} />
					</div>
				</div>

				{view === "threads" ? (
					<div className="pa-threads">
						{threads.length === 0 && (
							<div className="pa-empty-card">
								<p>Nenhuma conversa ainda.</p>
								<button type="button" className="pa-chip" onClick={handleNewConversation}>
									Começar agora
								</button>
							</div>
						)}
						{threads.map((t) => (
							<button
								key={t.id}
								type="button"
								className={`pa-thread-item${activeThreadId === t.id ? " is-active" : ""}`}
								onClick={() => {
									setActiveThreadId(t.id);
									setView("chat");
								}}
							>
								<span className="pa-thread-icon" aria-hidden>
									<SparkIcon size={14} />
								</span>
								<span className="pa-thread-title">{t.title || `Conversa #${t.id}`}</span>
							</button>
						))}
					</div>
				) : (
					<>
						<div className="pa-messages">
							{loading && (
								<div className="pa-typing">
									<span />
									<span />
									<span />
								</div>
							)}
							{!loading && messages.length === 0 && (
								<div className="pa-welcome">
									<div className="pa-welcome-icon" aria-hidden>
										<SparkIcon size={28} />
									</div>
									<h3>Como posso ajudar?</h3>
									<p>
										Posso ensinar a usar o PluggMe (tickets, massa, conexões…), buscar tickets e pesquisar na web.
									</p>
									<div className="pa-suggestions">
										{SUGGESTIONS.map((s) => (
											<button
												key={s}
												type="button"
												className="pa-chip"
												disabled={blocked || sending}
												onClick={() => handleSend(s)}
											>
												{s}
											</button>
										))}
									</div>
								</div>
							)}
							{messages.map((m) => {
								const isAssistant = m.role === "assistant";
								return (
									<div
										key={m.id}
										className={`pa-row pa-row--${isAssistant ? "assistant" : "user"}`}
									>
										{isAssistant ? (
											<div className="pa-avatar pa-avatar--ai pa-avatar--sm" aria-hidden>
												<SparkIcon size={12} />
											</div>
										) : (
											<div className="pa-avatar pa-avatar--user pa-avatar--sm" aria-hidden>
												{userInitial}
											</div>
										)}
										<div className={`pa-bubble pa-bubble--${isAssistant ? "assistant" : "user"}`}>
											{isAssistant
												? renderMessageContent(m.content, openTicket)
												: m.content}
										</div>
									</div>
								);
							})}
							{sending && (
								<div className="pa-row pa-row--assistant">
									<div className="pa-avatar pa-avatar--ai pa-avatar--sm" aria-hidden>
										<SparkIcon size={12} />
									</div>
									<div className="pa-bubble pa-bubble--assistant pa-bubble--typing">
										<div className="pa-typing">
											<span />
											<span />
											<span />
										</div>
									</div>
								</div>
							)}
							<div ref={bottomRef} />
						</div>
						<footer className="pa-composer">
							{blocked && (
								<p className="pa-composer-hint">Cota ou tokens esgotados. Peça liberação ao admin.</p>
							)}
							<div className="pa-composer-box">
								<textarea
									ref={textareaRef}
									value={input}
									onChange={(e) => setInput(e.target.value)}
									rows={1}
									placeholder="Pergunte algo…"
									disabled={sending || blocked}
									onKeyDown={(e) => {
										if (e.key === "Enter" && !e.shiftKey) {
											e.preventDefault();
											handleSend();
										}
									}}
								/>
								<button
									type="button"
									className="pa-send"
									disabled={sending || !input.trim() || blocked}
									onClick={() => handleSend()}
									aria-label="Enviar"
									title="Enviar"
								>
									<IconSend />
								</button>
							</div>
						</footer>
					</>
				)}
			</aside>
		</>
	);
}

export default PersonalAssistant;
